import { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import type { AgentSummary } from '@/store/auth';

// Zod schemas for the `/api/v1/auth/login` request/response. Both shapes are
// taken from `hub/app/api/auth.py` (`LoginRequest`, `LoginResponse`, `AgentOut`).
// Validating the response (vs. raw casting) means a future backend regression
// surfaces as a typed error here, not as undefined-property crashes deep in
// the auth store.

export const loginRequestSchema = z.object({
  email: z.string().email({ message: 'Enter a valid email address' }),
  password: z.string().min(1, { message: 'Password is required' }),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

const agentSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  display_name: z.string(),
  role: z.enum(['agent', 'admin']),
});

export const loginResponseSchema = z.object({
  access_token: z.string().min(1),
  token_type: z.literal('bearer'),
  agent: agentSchema,
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

/**
 * `POST /api/v1/auth/login` — exchange credentials for a JWT + agent profile.
 *
 * Thin wrapper: no auth store mutation here, no localStorage writes. The
 * mutation caller (LoginPage) is responsible for `useAuthStore.setSession(...)`
 * so the network call stays pure and trivially mockable in tests.
 */
export async function login(body: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post('/auth/login', body);
  return loginResponseSchema.parse(response.data);
}

// Re-export AgentSummary for callers that only want the type without pulling
// in the store module.
export type { AgentSummary };
